import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../lib/api.js";

/**
 * Jedno pobranie z API z licznikiem zadan (odpowiedz starsza niz ostatnio zastosowana jest pomijana, bo przy szybkiej
 * zmianie filtrow odpowiedzi moga wrocic w zlej kolejnosci), odswiezaniem co `co` ms i cichym odswiezaniem (S-7: bez
 * migotania i bez skoku ukladu).
 *
 * Hook zwraca ostatnia UDANA odpowiedz takze wtedy, gdy pobieramy nowa albo pobranie padlo, razem z `kluczDanych`: kluczem,
 * pod ktorym ta odpowiedz pobrano. To STRONA decyduje, czy dane pasuja do tego, co uzytkownik wybral (jak w KosztyPage):
 * lista pokazuje stare wiersze do czasu nowych, bo samoopisuja sie zakresem, a szczegol innego wywolania nie moze wisiec
 * pod nowym adresem.
 */

export interface StanZasobu<T> {
  dane: T | null;
  /** Klucz, pod ktorym pobrano `dane` (null przed pierwsza odpowiedzia). */
  kluczDanych: string | null;
  /** True przy pobraniu po zmianie klucza i przy pierwszym; nie dotyczy cichych odswiezen. */
  ladowanie: boolean;
  /** Kod bledu z serwera albo "siec". Null, gdy ostatnie pobranie sie udalo. */
  blad: string | null;
  /** Status HTTP ostatniego bledu (np. 404), albo null. */
  statusBledu: number | null;
  odswiez: () => void;
}

export function useZasob<T>(klucz: string, pobierz: () => Promise<T>, opcje: { co?: number } = {}): StanZasobu<T> {
  const [dane, setDane] = useState<T | null>(null);
  const [kluczDanych, setKluczDanych] = useState<string | null>(null);
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);
  const [statusBledu, setStatusBledu] = useState<number | null>(null);
  const [wersja, setWersja] = useState(0);
  const numerZapytania = useRef(0);
  const zastosowany = useRef(0);
  // Funkcja pobierajaca zmienia sie przy kazdym renderze (zamyka sie na filtrach), a efekt ma sie uruchamiac tylko przy
  // zmianie klucza: dlatego trzymamy ja w refie.
  const pobierzRef = useRef(pobierz);
  pobierzRef.current = pobierz;

  const uruchom = useCallback(
    (cicho: boolean) => {
      const numer = ++numerZapytania.current;
      if (!cicho) setLadowanie(true);
      pobierzRef
        .current()
        .then((odpowiedz) => {
          if (numer < zastosowany.current) return;
          zastosowany.current = numer;
          setDane(odpowiedz);
          setKluczDanych(klucz);
          setBlad(null);
          setStatusBledu(null);
        })
        .catch((err: unknown) => {
          if (numer < zastosowany.current) return;
          zastosowany.current = numer;
          setBlad(err instanceof ApiError ? err.code : "siec");
          setStatusBledu(err instanceof ApiError ? err.status : null);
        })
        .finally(() => {
          if (numer === numerZapytania.current) setLadowanie(false);
        });
    },
    [klucz],
  );

  useEffect(() => {
    uruchom(false);
  }, [uruchom, wersja]);

  const co = opcje.co;
  useEffect(() => {
    if (!co) return;
    const zegar = setInterval(() => uruchom(true), co);
    return () => clearInterval(zegar);
  }, [co, uruchom]);

  const odswiez = useCallback(() => setWersja((w) => w + 1), []);
  return { dane, kluczDanych, ladowanie, blad, statusBledu, odswiez };
}
